/**
 * Canonical registry of LLM pipeline **operation types** — the coarse unit a
 * pipeline-config `operations` map is keyed by.
 *
 * Distinct from prompt types (see promptTypes.ts): ONE operation runs a
 * pipeline that may execute several prompt types of different kinds. This is
 * the single source of truth for the operation-type LIST; admin UIs derive
 * their operation selectors, labels, and descriptions from it, so adding a new
 * operation here makes it appear everywhere.
 */

export interface OperationTypeMeta {
    /** Human label shown in admin selectors and headings. */
    label: string;
    /** One-line description of what configuring this operation controls. */
    description: string;
    /** The config UI shows a single inline run instead of the full
     *  prompt × provider table. Only realtime voice is single-run by design
     *  (exactly one OpenAI Realtime session per submission — nothing to fan
     *  out). Every other operation supports multiple prompt × provider runs. */
    singleRun: boolean;
}

export const OPERATION_TYPES = {
    analysis: {
        label: 'Solution Analysis',
        description: 'Configure how student solutions are analyzed: which pipelines are active, which prompt+provider combinations to use, and which is primary.',
        singleRun: false,
    },
    hint: {
        label: 'Hint',
        description: 'Configure prompt+provider combinations for generating hints. The primary combo is used for the response sent to the user.',
        singleRun: false,
    },
    textchat: {
        label: 'Text Chat',
        description: 'Configure prompt+provider combinations for each text chat step. The primary combo is returned to the student.',
        singleRun: false,
    },
    realtimeVoice: {
        label: 'Realtime Voice',
        description: 'Configure the system prompt for OpenAI Realtime voice sessions.',
        singleRun: true,
    },
    conceptLab: {
        label: 'Concept Lab',
        description: 'Configure prompt+provider for Concept Lab (post-solution deep learning). Supports targeting by question type and target exam.',
        singleRun: false,
    },
    learningMoments: {
        label: 'Learning Moments Detection',
        description: 'Configure prompt+provider for detecting learning moment patterns in student solutions. Runs async after analysis.',
        singleRun: false,
    },
    learningMomentsSitting: {
        label: 'Learning Moments Detection (whole sitting)',
        description: "Configure the prompt+provider that detects learning moments across EVERY analysed question of one completed sitting in a single call, instead of one call per question. The candidate catalogue — most of every detection prompt — is sent once per call; questions are chunked when a sitting is long. Which implementation runs for a student is the 'learning_moment_sitting_detect' gate; the manual re-detect on the paper's sittings page follows the same gate. See docs/FeatureDesigns/learning-moments/detection-control-vision.md.",
        singleRun: false,
    },
    judgeAnswerEquiv: {
        label: 'Judge — Answer Equivalence',
        description: 'The LLM-as-judge pass that decides whether a produced answer MEANS the same as the labelled one, re-scoring low/medium-confidence answer dimensions during scenario-run scoring. Kept independent of the model under test so judging stays consistent across compared runs.',
        singleRun: false,
    },
    // A separate operation from `judgeAnswerEquiv`, not a second pipeline inside
    // it.
    // Every enabled pipeline in an operation runs, and the primary one supplies
    // the result — so two judges sharing one operation means each caller can
    // receive the other's output. They answer different questions, they are
    // invoked from different code paths, and they parse different response
    // shapes; they are two operations.
    judgeQuality: {
        label: 'Judge — Assessment Quality',
        description: 'The LLM-as-judge pass that grades ONE prompt execution — was this analysis good enough to ship — on the same three-valued scale a human reviewer uses, so human/judge agreement is measurable. Used by the assessment bench and by assignment judging.',
        singleRun: false,
    },
    generateVariations: {
        label: 'Question Generation (from PDF)',
        description: 'Configure the prompt+provider that authors question variations from uploaded PDF page images (teacher question generation). Runs async via the generate-variations job.',
        singleRun: false,
    },
    // A separate operation from `generateVariations`, not a second pipeline
    // inside it: the input is question records rather than page images, the
    // prompt is text-only, and the two are invoked from different jobs. Sharing
    // one operation would mean each caller could receive the other's output.
    generateVariationsFromQuestions: {
        label: 'Question Generation (from questions)',
        description: 'Configure the prompt+provider that authors variations of questions we already hold — same concepts and method, different values. Seeded with the source question text, answer and solution rather than page images. Runs async via the generate-variations-from-questions job.',
        singleRun: false,
    },
    extractVerbatim: {
        label: 'Question Extraction (PDF)',
        description: 'Configure the prompt+provider that extracts the exact questions verbatim from an uploaded PDF (or link) into draft teacher questions (Create Assignment from PDF). Runs async via the extract-questions-from-pdf job.',
        singleRun: false,
    },
    extractVerbatimText: {
        label: 'Question Extraction (Word)',
        description: 'Configure the prompt+provider that extracts questions verbatim from an uploaded Word (.docx) — text + inline image tokens, no vision — into draft teacher questions (Create Assignment from a document). Runs async via the extract-questions-from-pdf job (docx branch).',
        singleRun: false,
    },
    enrichQuestions: {
        label: 'Question Enrichment (extracted)',
        description: 'Configure the prompt+provider that authors the solutions, hints and correct answer for each question extracted from a teacher PDF/Word file — worksheets rarely print them, and without this the published assignment has nothing for a student to check against. Runs once per question, async, as the second pass of the extract-questions-from-pdf job.',
        singleRun: false,
    },
    classifyQuestions: {
        label: 'Question Classification (extracted)',
        description: 'Configure the prompt+provider that files each question extracted from a teacher PDF/Word file under a curriculum topic and judges its difficulty — every question of one subject in one call, apart from the enrichment calls that solve them, so a solve that fails never costs a question its topic. Runs inside the extract-questions-from-pdf job when AppSettings.classifyQuestionsSeparately is on. See docs/FeatureDesigns/assignments-tests/paper-import-classification-target.md.',
        singleRun: false,
    },
    extractMarkScheme: {
        label: 'Mark Scheme Extraction',
        description: 'Configure the prompt+provider that reads an uploaded mark-scheme document (PDF via vision, or Word .docx via text) into per-question tariffs, accepted answers and M/A/B credit items. The rubric is then joined to the assignment\'s questions and cross-checked against the paper\'s own printed marks before anything is stored, so a misread tariff never marks students. Runs async via the extract-mark-scheme job. See docs/FeatureDesigns/AssessmentAndMarking/marking-scheme-upload.md.',
        singleRun: false,
    },
    extractAnswerKey: {
        label: 'Answer Key Extraction',
        description: 'Configure the prompt+provider that reads the separate answer & solution key a teacher uploads with a paper (PDF via vision, page by page with the next page as context; Word .docx via text) into rows of question number, printed answer and printed worked solution. The rows are matched to the paper\'s questions by number in code. Runs inside the extract-questions-from-pdf job when the teacher chose "Read them from my answer key". An answer key SHEET is instead read one page per call, at full image detail, and twice: the step\'s run reads through a parallel set of two models, and the reconcile_answer_key step keeps only answers both readings agree on (the rest are flagged "Key unclear"). See docs/FeatureDesigns/PipelineInfra/reconciled-readings-target.md and docs/FeatureDesigns/assignments-tests/paper-answer-key-target.md.',
        singleRun: false,
    },
    markAttempt: {
        label: 'Attempt Marking (one-shot)',
        description: "Configure the prompt+provider that marks a student's COMPLETED attempt against the uploaded mark-scheme document in a single call — the whole scheme is passed as-is (no per-question parse) alongside a text rendering of the student's already-extracted working. The model returns the scheme's credit items with a met/not-met verdict and the evidence for each; the marks themselves are computed in code from those verdicts, so every mark stays traceable. Runs async once the whole attempt is analysed. See docs/FeatureDesigns/AssessmentAndMarking/one-shot-marking.md.",
        singleRun: false,
    },
    splitSolutions: {
        label: 'Solution Splitter',
        description: "Configure the prompt+provider that reads a student's multi-page handwritten worksheet and matches each solution to the right question (bulk-solution-upload auto mode). Runs async via the solution-splitter job.",
        singleRun: false,
    },
    readAnswer: {
        label: 'Answer Second Read',
        description: "Configure the prompt+provider that reads ONLY the final answer off a student's uploaded page, independently of the solution splitter and extract_visuals and blind to the answer key — the uncropped page at high media resolution, located by the question number the student wrote. Observation only: runs async beside the analysis and changes no score. See docs/FeatureDesigns/solution-analysis/answer-second-read-target.md.",
        singleRun: false,
    },
    readAnswerList: {
        label: 'Answer List Read',
        description: "Configure the prompt+provider that reads a page the student marked as an answer list — that page alone, one call per page, at high media resolution — into the answers written against question numbers. The split still reads the working on it. Runs inside the solution-splitter job, beside the split, behind the solution_answer_list_step gate. See docs/FeatureDesigns/assignments-tests/answer-sheet-reader-target.md §4.",
        singleRun: false,
    },
    readSolutionPages: {
        label: 'Solution Page Reader',
        description: "Configure the prompt+provider that reads each page of a student's uploaded solution ONCE, beside the solution splitter, with no question context — no question text, options or answer key. It returns each page as blocks of work under the number the student wrote, each in the extract_visuals shape; code joins a block to its question, and the first analysis of that question uses it in place of a per-question extract_visuals call. Pages per call and concurrency are in Settings → Defaults. Runs inside the solution-splitter job, behind the solution_page_reader gate. See docs/FeatureDesigns/solution-analysis/solution-page-reader-target.md.",
        singleRun: false,
    },
    testReport: {
        label: 'Test Report',
        description: 'Configure the prompt+provider that writes the report a student reads after a TEST. The model writes prose only — every figure comes from a ledger computed in code and is checked back against it before the report is stored. Runs once the whole sitting is analysed.',
        singleRun: false,
    },
    questionSessionReport: {
        label: 'Question Session Report',
        description: 'Configure the prompt+provider that synthesises a submitted question session (assignment attempt) into a concise student-facing report — strengths, actionable focus areas, and the topic trajectory. Runs async once the whole attempt is analysed.',
        singleRun: false,
    },
    clusterMisconceptions: {
        label: 'Cluster Cohort Misconceptions',
        description: "Configure prompt+provider for grouping a class's solution-analysis observations into shared misconceptions. The model only groups; every count a teacher sees is computed from the real observations.",
        singleRun: false,
    },
    chemistryLabMix: {
        label: 'Chemistry Lab — Mix',
        description: 'Configure the prompt+provider that computes the outcome (color/gas/precipitate/temperature/pH + narration) of one free-form chemistry-lab mix not already in the curated reaction cache.',
        singleRun: false,
    },
    compileScene: {
        label: 'Question Scene — Compile',
        description: "Configure the prompt+provider that turns a question into a scene: its setup in one bench's vocabulary and which readouts it asks for, or an honest \"cannot\" with a reason. The model only translates — the bench's own engine computes every number, and the result is checked against the answer key before anyone sees it. See docs/FeatureDesigns/question-scenes/question-scenes-target.md.",
        singleRun: false,
    },
    chemistryLabExcess: {
        label: 'Chemistry Lab — Excess',
        description: 'Configure the prompt+provider for the quantitative bench\'s overlay: what a mix LOOKS like when one reagent is left over — pink fading past an endpoint, limewater going clear again, chips sitting in acid that has stopped fizzing. Asked in a band our own arithmetic computed ("excess" or "large excess") and never in amounts, so it stays cacheable; a stoichiometric mix does not call it at all.',
        singleRun: false,
    },
} as const satisfies Record<string, OperationTypeMeta>;

export type OperationType = keyof typeof OPERATION_TYPES;

/**
 * Which step types may legitimately appear in each operation's pipelines.
 *
 * The authority for "is this step filed under the right operation" — NOT the
 * admin's PIPELINE_TEMPLATES, which is a menu of suggested pipelines and is
 * routinely behind (it omits the structured-analysis steps that the live
 * default config has been running in production for months).
 *
 * Mostly this is `STEP_TYPE_FAMILY` inverted: analysis takes the analyze +
 * extract + compute families, hint takes hint + extract (both can be preceded
 * by an extraction step). The 'generate' family is the exception — six
 * operations share it, one step type each — so those are listed explicitly.
 *
 * Adding a step type to an operation means adding it here, once.
 */
export const OPERATION_STEP_TYPES: Record<OperationType, readonly string[]> = {
    analysis: [
        'extract_steps', 'extract_visuals',
        'analyze_image_text_objective', 'analyze_image_text_freeform',
        'analyze_text_objective', 'analyze_text_freeform',
        'analyze_visual_objective', 'analyze_visual_freeform', 'analyze_visual_structured',
        'analyze_descriptive',
        'score_objective', 'score_freeform', 'score_structured', 'score_descriptive',
    ],
    hint: ['extract_steps', 'extract_visuals', 'hint_for_image_text', 'hint_for_text', 'hint_for_visual'],
    textchat: ['textchat_greet', 'textchat_guide', 'textchat_summarize'],
    realtimeVoice: ['voice_realtime'],
    conceptLab: ['concept_lab'],
    learningMoments: ['detect_learning_moments'],
    learningMomentsSitting: ['detect_learning_moments_sitting'],
    judgeAnswerEquiv: ['eval_judge_answer_equivalence'],
    judgeQuality: ['eval_judge_assessment_quality'],
    generateVariations: ['generate_variations'],
    generateVariationsFromQuestions: ['generate_variations_from_questions'],
    // The optional judge step rides in the SAME operation as the extraction it
    // checks — it needs the page images and the transcription from the step
    // before it, both of which only exist inside that pipeline run.
    // See docs/FeatureDesigns/extraction-validation-judge.md.
    // The structured sibling of each extraction step is listed in the SAME
    // operation, not in one of its own: a paper is extracted by one operation
    // whose pipelines are targeted on questionType, so an admin sees "extract
    // verbatim" with a flat pipeline and a multi-part pipeline rather than two
    // operations that differ only in the shape they return.
    // See docs/FeatureDesigns/assignments-tests/structured-question-extraction-target.md.
    extractVerbatim: ['extract_verbatim', 'extract_verbatim_structured', 'merge_extraction_readings', 'locate_diagrams', 'validate_extraction'],
    extractVerbatimText: ['extract_verbatim_text', 'extract_verbatim_text_structured', 'validate_extraction'],
    enrichQuestions: ['enrich_questions'],
    classifyQuestions: ['classify_questions'],
    splitSolutions: ['split_solutions'],
    readAnswer: ['read_answer'],
    readAnswerList: ['read_answer_list'],
    readSolutionPages: ['read_solution_pages'],
    extractMarkScheme: ['extract_mark_scheme'],
    extractAnswerKey: ['extract_answer_key', 'reconcile_answer_key'],
    markAttempt: ['mark_attempt_one_shot'],
    questionSessionReport: ['generate_question_session_report'],
    testReport: ['generate_test_report'],
    clusterMisconceptions: ['cluster_misconceptions'],
    chemistryLabMix: ['chemistry_lab_mix'],
    chemistryLabExcess: ['chemistry_lab_excess'],
    compileScene: ['compile_scene'],
};

/** Is this step type legitimate for this operation? Unknown operations are
 *  given the benefit of the doubt — a caller shouldn't fail on a key this
 *  registry hasn't learned about yet. */
export function isStepTypeForOperation(operation: string, stepType: string): boolean {
    const allowed = (OPERATION_STEP_TYPES as Record<string, readonly string[]>)[operation];
    return !allowed || allowed.includes(stepType);
}

/**
 * Which operations run a given step type — `OPERATION_STEP_TYPES` inverted.
 *
 * Most step types answer with exactly one operation. Three do not, and that is
 * the model working rather than a mistake: `extract_steps` / `extract_visuals`
 * are the shared pre-step of both analysis and hint, and `validate_extraction`
 * judges either extraction flavour. A UI that groups prompts under operations
 * must therefore expect to list one prompt twice.
 *
 * An empty answer means the step type runs OUTSIDE the pipeline-config
 * `operations` map — nothing does today.
 */
export function operationsForStepType(stepType: string): OperationType[] {
    return OPERATION_TYPE_KEYS.filter(op => OPERATION_STEP_TYPES[op].includes(stepType));
}

/** Ordered list of all operation-type keys (insertion order = display order). */
export const OPERATION_TYPE_KEYS = Object.keys(OPERATION_TYPES) as OperationType[];

/** Label for an operation type, falling back to the raw key. */
export function operationLabel(op: string): string {
    return (OPERATION_TYPES as Record<string, OperationTypeMeta>)[op]?.label ?? op;
}
